import { eachMonthOfInterval, format, isSameMonth, isWeekend } from 'date-fns';

export type DayStatus =
  | 'Present'
  | 'ShortDay'
  | 'VeryShort'
  | 'Missing'
  | 'WFH'
  | 'FullDayLeave'
  | 'HalfDayLeave'
  | 'PublicHoliday';

export interface DayRecord {
  date: string;
  status: DayStatus;
  arrival: string;
  departure: string;
  hours: string;
  publicHolidayName?: string;
}

export const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export const STATUS_LABELS: Record<DayStatus, string> = {
  Present: 'Present',
  ShortDay: 'Short Day',
  VeryShort: 'Too Short',
  Missing: 'Missing',
  WFH: 'WFH',
  FullDayLeave: 'Full Day Leave',
  HalfDayLeave: 'Half Day Leave',
  PublicHoliday: 'Mercantile Holiday'
};

export const STATUS_CLASS: Record<DayStatus, string> = {
  Present: 'present',
  ShortDay: 'short-day',
  VeryShort: 'very-short',
  Missing: 'missing',
  WFH: 'wfh',
  FullDayLeave: 'full-day-leave',
  HalfDayLeave: 'half-day-leave',
  PublicHoliday: 'public-holiday'
};

export const formatTimeRange = (record: DayRecord) => {
  if (record.arrival !== '—' && record.departure !== '—') {
    return `${record.arrival} → ${record.departure}`;
  }
  return '';
};

export const formatHoursLine = (record: DayRecord) => {
  if (record.hours !== '—' && record.hours.length > 0) {
    return `Hours: ${record.hours}`;
  }
  return '';
};

export const formatTooltipHours = (record: DayRecord) => {
  if (record.hours === '—') return '—';
  return record.hours.replace('h', ' hrs');
};

export const parseHours = (value: string) => {
  if (!value || value === '—') return 0;

  const hoursMinutesMatch = value.match(/(\d+)\s*h(?:\s*(\d+)\s*m)?/i);
  if (hoursMinutesMatch) {
    const hours = Number.parseInt(hoursMinutesMatch[1], 10);
    const minutes = hoursMinutesMatch[2]
      ? Number.parseInt(hoursMinutesMatch[2], 10)
      : 0;
    return hours + minutes / 60;
  }

  const match = value.match(/([\d.]+)/);
  return match ? Number.parseFloat(match[1]) : 0;
};

export const getMonthsInView = (rangeStart: Date, rangeEnd: Date) =>
  eachMonthOfInterval({ start: rangeStart, end: rangeEnd });

export const formatViewRangeLabel = (rangeStart: Date, rangeEnd: Date) =>
  `${format(rangeStart, 'd MMM yyyy')} — ${format(rangeEnd, 'd MMM yyyy')}`;

export const isDateInViewRange = (
  date: Date,
  rangeStart: Date,
  rangeEnd: Date
) => date >= rangeStart && date <= rangeEnd;

export { isSameMonth, isWeekend };
